import type {
  ChangeEventHandler,
  ClipboardEventHandler,
  HTMLProps,
  KeyboardEvent,
  Ref
} from 'react';
import React, {
  useCallback,
  forwardRef,
  useImperativeHandle,
  useState
} from 'react';

import './index.scss';
// import Picture from '@components/Picture';
import { Picture } from '@ui';
import icons from '@icons';
import InputMask from '@components/InputMask';
import clsx from 'clsx';

const {
  eye: { midGrey: eyeOutlinedMidGrey },
  eyeOff: { midGrey: eyeOffOutlinedMidGrey },
  search: { darkGrey: searchIconOutlinedDarkGrey },
  close: { midGrey: closeOutlinedMidGrey }
} = icons.Outline;

const normalizeToLocalPhone = (rawValue: string): string | null => {
  const digits = rawValue.replace(/\D/g, '');

  if (digits.length === 10 && digits[0] === '9') {
    return digits;
  }

  if (
    digits.length === 11 &&
    (digits[0] === '7' || digits[0] === '8') &&
    digits[1] === '9'
  ) {
    return digits.slice(1);
  }

  return null;
};

interface InputProps extends HTMLProps<HTMLInputElement> {
  autoComplete?: string;
  name: string;
  value: string;
  error?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  getInputData: ({
    name,
    value,
    error
  }: {
    name: string;
    value: string;
    error?: string;
  }) => void;
  field?: string;
  autoFocus?: boolean;
  inputClassName?: string;
  inputRef?: Ref<HTMLInputElement>;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}
const Input = forwardRef<any, InputProps>(
  (
    {
      autoComplete,
      name,
      inputClassName,
      value,
      error,
      placeholder,
      className = '',
      disabled,
      getInputData,
      field,
      autoFocus,
      inputRef,
      onKeyDown = () => {},
      ...props
    },
    ref
  ) => {
    const [isInputFocused, setIsInputFocused] = useState(false);
    const onInputFocus = (): void => {
      setIsInputFocused(true);
    };
    const onInputBlur = (): void => {
      setIsInputFocused(false);
    };
    const onInputChange: ChangeEventHandler<HTMLInputElement> = e => {
      getInputData({ name, value: e.target.value });
    };
    const [showPassword, setShowPassword] = useState(false);
    const toggleShowPassword = (): void => {
      setShowPassword(!showPassword);
    };

    const onPhoneInputChange: ChangeEventHandler<HTMLInputElement> = e => {
      const rawValue = e.target.value;
      const phone = rawValue.replace(/\D/g, '');
      getInputData({ name, value: phone });
    };

    const onPhonePaste: ClipboardEventHandler<HTMLInputElement> = e => {
      const pastedValue = e.clipboardData.getData('text');
      const normalizedPhone = normalizeToLocalPhone(pastedValue);

      if (normalizedPhone) {
        e.preventDefault();
        getInputData({ name, value: normalizedPhone });
      }
    };

    const isInputFocusedOrHasValue = isInputFocused || value?.length > 0;
    const inputFocusClassname = `${
      isInputFocusedOrHasValue ? ' InputWrapper__input--focused' : ''
    }`;

    const clearInput = useCallback((): void => {
      if (value) {
        getInputData({ name, value: '', error: '' });
      }
    }, [getInputData, name, value]);

    useImperativeHandle(
      ref,
      () => ({
        clearInput
      }),
      [clearInput]
    );
    return (
      <div
        className={`InputWrapper${error ? ' InputWrapper--error' : ''}${
          isInputFocusedOrHasValue ? ' InputWrapper--focused' : ''
        }${disabled ? ' InputWrapper--disabled' : ''} ${className || ''}`}
      >
        {(isInputFocusedOrHasValue || error) && (
          <div className='InputWrapper__placeholder'>
            {error || placeholder}
          </div>
        )}

        {/* Чтобы не было автофокуса */}
        <input type='radio' style={{ display: 'none' }} />
        {/* Чтобы не было автофокуса */}

        {field === 'text' ? (
          <input
            className={clsx(
              `InputWrapper__input`,
              inputFocusClassname,
              inputClassName
            )}
            type='text'
            name={name || ''}
            autoComplete={name && autoComplete !== 'off' ? 'on' : 'off'}
            disabled={disabled}
            value={value}
            placeholder={isInputFocusedOrHasValue ? '' : placeholder}
            onChange={onInputChange}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            autoFocus={autoFocus}
            onKeyDown={onKeyDown}
            {...props}
          />
        ) : field === 'phone' ? (
          <InputMask
            inputRef={inputRef}
            value={value}
            className={clsx(
              `InputWrapper__input`,
              inputFocusClassname,
              inputClassName
            )}
            type='tel'
            placeholder={placeholder}
            name='phone'
            autoComplete={autoComplete}
            mask='+7 (999) 999-99-99'
            maskChar=''
            onChange={onPhoneInputChange}
            onPaste={onPhonePaste}
            onBlur={onInputBlur}
            onFocus={onInputFocus}
            autoFocus={autoFocus}
            onKeyDown={onKeyDown}
          />
        ) : field === 'code' ? (
          <input
            type='number'
            className={clsx(
              `InputWrapper__input InputWrapper__input--code`,
              inputFocusClassname,
              inputClassName
            )}
            value={value}
            autoComplete='off'
            placeholder={isInputFocusedOrHasValue ? '' : '• • • •'}
            onChange={onInputChange}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            onKeyDown={onKeyDown}
            {...props}
          />
        ) : field === 'pass' ? (
          <div className='InputWrapper__pass-wrapper'>
            <input
              className={clsx(
                `InputWrapper__input InputWrapper__input--code`,
                inputFocusClassname,
                inputClassName
              )}
              value={value}
              placeholder={isInputFocusedOrHasValue ? '' : placeholder}
              onChange={onInputChange}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              name={name || 'pass'}
              autoComplete={autoComplete === 'on' ? 'on' : 'off'}
              type={showPassword ? 'text' : 'password'}
              onKeyDown={onKeyDown}
              {...props}
            />
            <Picture
              className='InputWrapper__pass-wrapper__eye-icon'
              onClick={toggleShowPassword}
              icon={showPassword ? eyeOutlinedMidGrey : eyeOffOutlinedMidGrey}
              height='24'
            />
          </div>
        ) : field === 'search' ? (
          <div className='InputWrapper__search-wrapper'>
            <Picture
              pictureClassName='InputWrapper__search-wrapper__icon'
              icon={searchIconOutlinedDarkGrey}
              height='24'
              width='24'
            />
            <input
              ref={ref}
              className={clsx(
                `InputWrapper__input`,
                inputFocusClassname,
                inputClassName
              )}
              type='text'
              value={value}
              placeholder={isInputFocusedOrHasValue ? '' : placeholder}
              onChange={onInputChange}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              onKeyDown={onKeyDown}
            />
            <Picture
              onClick={clearInput}
              pictureClassName='InputWrapper__search-wrapper__cross'
              icon={closeOutlinedMidGrey}
              height='24'
              width='24'
            />
          </div>
        ) : null}
      </div>
    );
  }
);

export default Input;
